import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import SettingsIcon from '@mui/icons-material/Settings';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Fade from '@mui/material/Fade';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useSelector } from 'react-redux';

import Button from '@/components/Button';
import Modal from '@/components/Modals/Modal';
import { RootState } from '@/redux/store';

type PluginStatus = 'connected' | 'disconnected' | 'error' | 'disabled';

interface PluginCardProps {
  name: string;
  logo: string;
  logoStyle?: React.CSSProperties;
  logoSuffix?: string;
  status: PluginStatus;
  onConnect: (name: string) => void;
  onDisconnect: () => void;
  onUpdateConfig: (name: string) => void;
}

const PluginCard = ({
  name,
  logo,
  logoStyle,
  logoSuffix,
  status,
  onConnect,
  onDisconnect,
  onUpdateConfig,
}: PluginCardProps) => {
  const theme = useTheme();
  const { profile } = useSelector((state: RootState) => state.parcel);
  const [disconectModal, setDisconectModal] = useState(false);
  const [hovered, setHovered] = useState(false);

  const mlDomain = (() => {
    switch (profile.data?.country?.toUpperCase()) {
      case 'MX':
      case 'MEX':
        return 'mercadolibre.com.mx';
      case 'PE':
      case 'PER':
        return 'mercadolibre.com.pe';
      case 'CO':
      case 'COL':
        return 'mercadolibre.com.co';
      case 'CL':
      case 'CHL':
        return 'mercadolibre.cl';
      default:
        return 'mercadolibre.com';
    }
  })();
  const mlAppsUrl = `https://www.${mlDomain}/accounts/my-apps`;
  const isConnected = status === 'connected';
  const isError = status === 'error';
  const isDisconnected = status === 'disconnected';
  const isDisabled = status === 'disabled';

  const statusLabel = {
    connected: 'Conectado',
    disconnected: 'Desconectado',
    error: 'Conexión fallida',
    disabled: 'Próximamente',
  };

  const statusColors = (() => {
    if (isConnected) {
      return {
        background: theme.palette.primary.lighter,
        text: theme.palette.primary.darker,
        dot: theme.palette.primary.main,
      };
    }
    if (isError) {
      return {
        background: theme.palette.dalia.light,
        text: theme.palette.dalia.main,
        dot: theme.palette.dalia.main,
      };
    }
    return {
      background: theme.palette.grey[50],
      text: theme.palette.secondary.main,
      dot: theme.palette.grey[500],
    };
  })();

  return (
    <>
      <Paper
        elevation={hovered && isDisconnected ? 6 : 3}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={isDisconnected ? () => onConnect(name) : undefined}
        className="relative flex h-28 w-full items-center justify-center overflow-hidden"
        sx={{
          border: isError ? `1px solid ${theme.palette.error.main}` : 'none',
          cursor: isDisconnected ? 'pointer' : 'default',
          transition: 'box-shadow 0.2s ease',
        }}
      >
        <div
          className="mt-4 flex items-center justify-center gap-3"
          style={{
            filter: isConnected || isError ? 'none' : 'grayscale(100%)',
            opacity: isConnected || isError ? 1 : 0.5,
          }}
        >
          <img src={logo} alt={name} style={{ height: 32, maxWidth: '160px', ...logoStyle }} />
          {logoSuffix && (
            <>
              <Typography component="span" sx={{ color: 'text.disabled', fontSize: '1.2rem' }}>
                |
              </Typography>
              <Typography
                component="span"
                sx={{
                  fontFamily: '"Proxima Nova", -apple-system, Roboto, Arial, sans-serif',
                  fontWeight: 'bold',
                  fontSize: '1.3rem',
                  color: '#abd037',
                  whiteSpace: 'nowrap',
                }}
              >
                {logoSuffix}
              </Typography>
            </>
          )}
        </div>

        <Chip
          size="small"
          label={statusLabel[status]}
          icon={
            <FiberManualRecordIcon
              sx={{ fontSize: '8px !important', color: `${statusColors.dot} !important` }}
            />
          }
          sx={{
            position: 'absolute',
            top: 10,
            left: 10,
            fontWeight: 600,
            fontSize: '0.75rem',
            backgroundColor: statusColors.background,
            color: statusColors.text,
          }}
        />

        {isDisconnected && (
          <Fade in={hovered} timeout={200} unmountOnExit>
            <div
              className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-1"
              style={{ backgroundColor: theme.palette.primary.main }}
            >
              <AddCircleOutlineIcon sx={{ color: '#fff', fontSize: 32 }} />
              <Typography variant="body2" sx={{ color: '#fff', fontWeight: 700 }}>
                Conectar plugin
              </Typography>
            </div>
          </Fade>
        )}

        {!isDisconnected && !isDisabled && (
          <Tooltip title="Desconectar plugin">
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                setDisconectModal(true);
              }}
              sx={{ position: 'absolute', top: 4, right: 4 }}
            >
              <InsertLinkIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {!isDisconnected && !isDisabled && (
          <Tooltip title="Ajustes">
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                onUpdateConfig(name);
              }}
              sx={{ position: 'absolute', bottom: 4, right: 4 }}
            >
              <SettingsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Paper>
      <Modal
        isOpen={disconectModal}
        onClose={() => setDisconectModal(false)}
        title={`¿Desconectar ${name}?`}
        actions={
          <Grid
            container
            sx={{
              display: 'flex',
              justifyContent: 'space-around',
              width: '100%',
              paddingBottom: '1rem',
              gap: '1rem',
            }}
          >
            <Button variant="outlined" onClick={() => setDisconectModal(false)}>
              Cancelar
            </Button>
            <Button
              onClick={() => {
                onDisconnect();
                setDisconectModal(false);
              }}
            >
              Desconectar
            </Button>
          </Grid>
        }
      >
        <Box sx={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <Typography variant="body1" textAlign="center">
            ¿Estás seguro de que deseas desconectar {name}?
          </Typography>
          {name.toLowerCase().includes('falabella') ? (
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Al desconectar este plugin, se pausará la sincronización de órdenes y la actualización
              automática de estatus logísticos.
            </Typography>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Para completar la desinstalación, también debes revocar el acceso desde tu cuenta de
                Mercado Libre:
              </Typography>
              <Typography variant="body2" textAlign="center">
                <Link href={mlAppsUrl} target="_blank" rel="noopener noreferrer">
                  {mlAppsUrl}
                </Link>
              </Typography>
            </>
          )}
        </Box>
      </Modal>
    </>
  );
};

export default PluginCard;
